import { useId, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';
import { useLocale, useT } from '../i18n/useT';
import { FieldError } from './FieldError';
import { countryOptions, matchesCountry, type CountryOption } from './phone';

interface PhoneFieldProps {
  label: string;
  country: string;
  number: string;
  error: string | undefined;
  onChange: (patch: { country?: string; number?: string }) => void;
}

const label = (o: CountryOption): string => `${o.name} (+${o.callingCode})`;

/** Country code (searchable select) next to a national number input. */
export const PhoneField = ({
  label: text,
  country,
  number,
  error,
  onChange
}: PhoneFieldProps): JSX.Element => {
  const t = useT();
  const locale = useLocale();
  const labelId = useId();
  const errorId = useId();
  const listId = useId();
  const optionId = (code: string): string => `${listId}-${code}`;
  const { preferred, others } = useMemo(() => countryOptions(locale), [locale]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const list = useRef<HTMLUListElement>(null);

  const selected = [...preferred, ...others].find((o) => o.code === country);
  const shownPreferred = preferred.filter((o) => matchesCountry(o, query));
  const shownOthers = others.filter((o) => matchesCountry(o, query));
  const shown = [...shownPreferred, ...shownOthers];
  const showSeparator = shownPreferred.length > 0 && shownOthers.length > 0;

  const close = (): void => {
    setOpen(false);
    setQuery('');
    setActive(0);
  };
  const choose = (o: CountryOption): void => {
    onChange({ country: o.code });
    close();
  };
  const moveTo = (index: number): void => {
    const next = Math.max(0, Math.min(shown.length - 1, index));
    setActive(next);
    const option = shown[next];
    if (option !== undefined) {
      const element = list.current?.querySelector<HTMLElement>(`[id="${optionId(option.code)}"]`);
      // jsdom has no scrollIntoView.
      if (typeof element?.scrollIntoView === 'function') {
        element.scrollIntoView({ block: 'nearest' });
      }
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) {
          moveTo(active + 1);
        } else {
          setOpen(true);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveTo(active - 1);
        break;
      case 'Enter':
        if (open) {
          // Enter picks a country; it must not submit the booking form.
          event.preventDefault();
          const option = shown[active];
          if (option !== undefined) {
            choose(option);
          }
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          close();
        }
        break;
      default:
    }
  };

  const renderOption = (o: CountryOption, index: number): JSX.Element => (
    <li
      key={o.code}
      id={optionId(o.code)}
      role="option"
      aria-selected={o.code === country}
      className={index === active ? 'ow-combo-option ow-combo-active' : 'ow-combo-option'}
      onMouseDown={(event) => {
        event.preventDefault();
        choose(o);
      }}
      onMouseMove={() => {
        setActive(index);
      }}
    >
      {label(o)}
    </li>
  );

  return (
    <div className="ow-field-group">
      <span id={labelId} className="ow-label">
        {text}
      </span>
      <div className="ow-phone">
        <div className="ow-combo">
          <input
            role="combobox"
            className="ow-field ow-phone-code"
            aria-label={t('Country code')}
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={open && shown[active] ? optionId(shown[active].code) : undefined}
            autoComplete="off"
            value={open ? query : selected === undefined ? '' : `+${selected.callingCode}`}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
              setOpen(true);
            }}
            onClick={() => {
              setOpen(true);
            }}
            onFocus={(e) => {
              setOpen(true);
              e.currentTarget.select();
            }}
            onBlur={close}
            onKeyDown={onKeyDown}
          />
          {open && (
            <ul
              ref={list}
              id={listId}
              role="listbox"
              className="ow-combo-list"
              aria-label={t('Country code')}
            >
              {shownPreferred.map((o, i) => renderOption(o, i))}
              {showSeparator && <li role="separator" className="ow-combo-separator" />}
              {shownOthers.map((o, i) => renderOption(o, i + shownPreferred.length))}
              {shown.length === 0 && (
                <li role="presentation" className="ow-combo-empty">
                  {t('No match')}
                </li>
              )}
            </ul>
          )}
        </div>
        <input
          className="ow-field ow-phone-number"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          aria-labelledby={labelId}
          aria-invalid={error !== undefined}
          aria-describedby={error === undefined ? undefined : errorId}
          value={number}
          onChange={(e) => {
            onChange({ number: e.target.value });
          }}
        />
      </div>
      <FieldError message={error} id={errorId} />
    </div>
  );
};
